import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonModal,
  IonPage,
  IonToolbar,
  createGesture,
} from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import Nav from './Nav';
import ContactCard from './ContactCard';
import HeroChrome from '../components/HeroChrome';
import HomeAlerts from '../components/HomeAlerts';
import { ChevronRight } from '../components/icons';
import {
  AccidentSvg,
  ChainOfContactSvg,
  DmPhoneSvg,
  FaqSvg,
  HandbookSvg,
  LicensePlateSvg,
  LoadHistorySvg,
  MoneySvg,
  NewReportSvg,
  OrientationReportSvg,
  PhoneSvg,
  RegistrationsSvg,
  ReleaseTraineeSvg,
  SafetyContactsSvg,
  TraineeProfileSvg,
} from '../components/svgIcons';
import { ApiService } from '../api/ApiService';
import { UserState, userStore } from '../api/AuthService';
import { DriverProfile } from '../api/model/DriverProfile';
import { DriverManager } from '../api/model/DriverManager';
import { PushNotificationsService } from '../api/PushNotificationsService';
import { sidnavStore, SidenavState } from '../api/SidenavStore';
import { UserUtils } from '../utils/UserUtils';
import { checkOS } from '../components/OSCheck';
import { toastStore } from '../components/AppToast';
import { runStatus } from '../components/walkthrough/FirstSignIn';
import { Capacitor } from '@capacitor/core';
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { config } from '../Constants';

function toProperCase(str: string) {
  if (!str) return '';
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

function formatDeliveryEta(deliveryEndTime?: string): string {
  if (!deliveryEndTime) return '—';
  const raw = String(deliveryEndTime).trim();
  if (!raw) return '—';
  const d = new Date(raw.includes('T') || raw.includes('-') ? raw : raw.replace(' ', 'T'));
  if (isNaN(d.getTime())) {
    return raw.replace(/,?\s*\d{4}\b/, '').replace(/\b\d{4}\b/, '').trim() || '—';
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? ' pm' : ' am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minStr = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${hours}${minStr}${ampm}`;
}

function useInView<T extends HTMLElement = HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        observer.disconnect();
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

type ContactButtonProps = {
  to?: string;
  onClick?: (e: React.MouseEvent) => void;
  label: string;
  icon: React.ReactNode;
  side: 'left' | 'right';
};

const ContactButton: React.FC<ContactButtonProps> = ({ to, onClick, label, icon, side }) => {
  const { ref, inView } = useInView<HTMLElement>();
  const className = `home-action home-action--contact home-contacts__btn--${side}${
    inView ? ' is-inview' : ''
  }`;
  const inner = (
    <>
      <div className="home-action__icon">{icon}</div>
      <div className="home-action__label">{label}</div>
    </>
  );
  if (to) {
    return (
      <Link ref={ref as any} to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button ref={ref as any} type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
};

const Home: React.FC = () => {
  const history = useHistory();
  const gestureEl = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [showRateInfo, setShowRateInfo] = useState(false);
  const [showDM, setShowDM] = useState(false);
  const [currentRate, setCurrentRate] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [dm, setDM] = useState<DriverManager>({} as DriverManager);

  const { driver, storedLoadInfo } = userStore((state: UserState) => ({
    driver: state.driver as DriverProfile,
    storedLoadInfo: state.loadInfo,
  }));
  const { close } = sidnavStore((state: SidenavState) => ({ close: state.close }));
  const { toggleSidenav } = sidnavStore((state: SidenavState) => ({ toggleSidenav: state.toggle }));

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    getAvailableAppVersion();
    if (config.showSidenav) {
      gestureInit();
    }
    console.log('os', checkOS());
    if (localStorage.getItem('pushNotificationPermission') === null) {
      const userId = userStore.getState().user?.id;
      if (userId) {
        PushNotificationsService.registerAfterAppLogin(userId);
      }
    }

    ApiService.chatConversationGet('home', {}).then((it: any) => {
      if (!Array.isArray(it)) return;
      let count = 0;
      for (let i = 0; i < it.length; i++) {
        if (it[i]?.lastMessage?.read !== 1) count += 1;
      }
      setMsgCount(count);
    }).catch(() => {});

    if (!UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector()) {
      ApiService.getPushNotifications('home')
        .then((it: any) => {
          if (!Array.isArray(it)) return;
          setNotificationCount(it.filter((notification: any) => !notification.read).length);
        })
        .catch(() => {});
    }

    if (userStore.getState().driver?.dmCode) {
      ApiService.getDriverManager('home', { dmCode: userStore.getState().driver.dmCode }).then(it => {
        if (it) setDM(it);
      });
    }

    if (localStorage.getItem('type') === 'trainee') {
      ApiService.getPayMatrix('home', {}).then((it: any) => {
        if (it == null) return;
        setCurrentRate(Math.round(Number(it) * 100) / 100);
      });
    }
  }, []);

  const getAvailableAppVersion = async () => {
    try {
      const result = await AppUpdate.getAppUpdateInfo();
      if (Capacitor.getPlatform() === 'android') {
        console.log('code', result.availableVersionCode);
      } else {
        console.log('name', result.availableVersionName);
      }
    } catch (error) {
      console.log('Error getting version', error);
    }
  };

  const gestureInit = () => {
    const card = gestureEl.current;
    if (!card) return;
    const gesture = createGesture({
      el: card,
      gestureName: 'swipeMenu',
      onMove: () => {},
      onEnd: () => {
        runStatus ? console.log('locked') : toggleSidenav();
      },
    });
    gesture.enable();
  };

  const greetingName = useMemo(() => {
    const name = driver?.name || '';
    const first = name.indexOf(' ') > 0 ? name.substring(0, name.indexOf(' ')) : name;
    return toProperCase(first);
  }, [driver]);

  const hasLoad = !!storedLoadInfo;
  const destination = hasLoad
    ? [storedLoadInfo!.consigneeCity, storedLoadInfo!.consigneeState].filter(Boolean).join(', ') || '—'
    : 'No active load';
  const loadNumberLabel = hasLoad
    ? `Load #${storedLoadInfo!.orderNumber || storedLoadInfo!.loadNumber || '—'}`
    : '';
  const loadOriginMeta = hasLoad
    ? `from ${[storedLoadInfo!.shipperCity, storedLoadInfo!.shipperState].filter(Boolean).join(', ') || '—'}`
    : 'Check Load for available trips';
  const toDestination =
    hasLoad && storedLoadInfo!.milesToDestination != null
      ? Number(storedLoadInfo!.milesToDestination)
      : null;
  const totalTrip = hasLoad && storedLoadInfo!.milesTotal != null ? Number(storedLoadInfo!.milesTotal) : null;
  const milesToGo =
    toDestination != null && !Number.isNaN(toDestination) ? String(Math.round(toDestination)) : '—';
  const totalTripMiles =
    totalTrip != null && !Number.isNaN(totalTrip) ? String(Math.round(totalTrip)) : '—';
  const estEta = hasLoad ? formatDeliveryEta(storedLoadInfo!.deliveryEndTime) : '—';

  const dmPhone = dm.phone || dm.dmPhone;
  const callAgent = () => {
    if (!dmPhone) {
      toastStore.getState().setToast('Agent phone number is not available.');
      return;
    }
    ApiService.sendEventCommand('Call Agent', 'eventCheck', 'User called their agent from home');
    window.location.href = `tel:${dmPhone}`;
  };

  const onHaulActivate = useCallback(() => {
    if (hasLoad) history.push('/currentload');
  }, [hasLoad, history]);

  const trainingTiles = !UserUtils.isDriver()
    ? [
        ...(!UserUtils.isTrainee()
          ? [
              { label: 'New Report', to: '/trainingday', icon: <NewReportSvg />, delay: 'home-delay-110' },
              { label: 'Release Trainee', to: '/releasetrainee', icon: <ReleaseTraineeSvg />, delay: 'home-delay-115' },
            ]
          : []),
        { label: 'Agreements', to: '/agreements', icon: <HandbookSvg />, delay: 'home-delay-120' },
        { label: 'Training Contacts', to: '/trainingcontacts', icon: <PhoneSvg />, delay: 'home-delay-125' },
        UserUtils.isTrainee()
          ? {
              label: 'Trainee Profile',
              to: '/traineeprofile',
              icon: <TraineeProfileSvg />,
              span2: !UserUtils.isTrainerFeedbackEnabled(),
              delay: 'home-delay-130',
            }
          : { label: 'Trainee Info', to: '/traineeprofile', icon: <TraineeProfileSvg />, span2: true, delay: 'home-delay-130' },
        ...(UserUtils.isTrainerFeedbackEnabled()
          ? [{ label: 'Trainer Feedback', to: '/trainerfeedback', icon: <FaqSvg />, span2: true, delay: 'home-delay-130' }]
          : []),
      ]
    : [];

  return (
    <IonPage>
      <IonContent fullscreen className="design-a-content">
        <HomeAlerts />

        <IonModal isOpen={showRateInfo} cssClass="small-modal" onDidDismiss={() => setShowRateInfo(false)}>
          <IonHeader>
            <IonToolbar color="primary">
              <p style={{ fontFamily: 'Montserrat, sans-serif', padding: '0 8px' }} onClick={() => setShowRateInfo(false)}>
                Close
              </p>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="item-container" style={{ padding: 16 }}>
              <p style={{ fontSize: 24, fontWeight: 600, color: '#666667', textAlign: 'center' }}>Rate Info</p>
              <p style={{ fontSize: 32, fontWeight: 600, textAlign: 'center' }}>
                {currentRate ? `$${currentRate}` : '—'}
              </p>
              <p className="subtitle">
                ***To be paid for the day, you must log drive time or on duty time on the tablet. If you are in
                sleeper birth or off duty all day, you will not be paid. If you have any questions, please contact
                your training manager.
              </p>
            </div>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showDM} id="transparent" onDidDismiss={() => setShowDM(false)}>
          <div className="modal-div" onClick={() => setShowDM(false)}>
            <div className="item-container" id="modal">
              <ContactCard
                contact={{
                  name: `${dm.firstName || ''} ${dm.lastName || ''}`.trim() || dm.dmName || 'Agent',
                  email: dm.email || dm.dmEmail,
                  subcategory: 'Driver Agent',
                  phone: dmPhone,
                }}
              />
            </div>
          </div>
        </IonModal>

        <div
          ref={gestureEl}
          className={`design-a${mounted ? ' design-a--mounted' : ''}`}
          onClick={() => close()}
          id="WalkThroughFeature0"
        >
          <div className="home-hero" id="home-hero">
            <HeroChrome
              onMenuClick={() => toggleSidenav()}
              msgCount={msgCount}
              notificationCount={notificationCount}
            />
            <div className="home-hero__body">
              <div className="home-hero__greeting">
                Hey {greetingName || 'Driver'}! · Unit {driver?.unit || '—'}
              </div>
              <div
                role="link"
                tabIndex={0}
                className="home-hero__content home-hero__content--haul-link"
                aria-label="View current load"
                onClick={onHaulActivate}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onHaulActivate();
                  }
                }}
              >
                <div className="home-hero__haul">
                  {loadNumberLabel ? <div className="home-hero__load-number">{loadNumberLabel}</div> : null}
                  <div className="home-hero__eyebrow">Currently hauling to</div>
                  <div className="home-hero__destination home-display">{destination}</div>
                  <div className="home-hero__meta">{loadOriginMeta}</div>
                </div>
                <div className="home-hero__chips">
                  <div className="home-hero__chips-row">
                    <div className="home-hero__chip">
                      <div className="home-hero__chip-value">{milesToGo}</div>
                      <div className="home-hero__chip-label">miles to go</div>
                    </div>
                    <div className="home-hero__chip">
                      <div className="home-hero__chip-value">{totalTripMiles}</div>
                      <div className="home-hero__chip-label">est. trip miles</div>
                    </div>
                  </div>
                  <div className="home-hero__chip home-hero__chip--eta">
                    <div className="home-hero__chip-value">{estEta}</div>
                    <div className="home-hero__chip-label">PTA</div>
                  </div>
                  <div className="home-hero__haul-hint">
                    tap for more
                    <ChevronRight color="#ffffff" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="home-sheet">
            <div className="load-options-grid" id="WalkThroughFeature1">
              {UserUtils.isTrainee() ? (
                <button
                  type="button"
                  className="load-option"
                  onClick={e => {
                    e.stopPropagation();
                    setShowRateInfo(true);
                  }}
                >
                  <div className="load-option__icon">
                    <MoneySvg />
                  </div>
                  <div>
                    <div className="load-option__title">Rate Info</div>
                    <div className="load-option__sub">View your current pay rate</div>
                  </div>
                </button>
              ) : null}

              <Link to="/registrations" className={`load-option${UserUtils.isTrainee() ? '' : ' load-option--span2'}`}>
                <div className="load-option__icon">
                  <RegistrationsSvg />
                </div>
                <div>
                  <div className="load-option__title">View Registrations</div>
                  <div className="load-option__sub">Vehicle registration status</div>
                </div>
              </Link>
            </div>

            {!UserUtils.isDriver() && !UserUtils.isTrainee() ? (
              <>
                <div className="home-section-label">Orientation</div>
                <div className="home-training-grid">
                  <Link to="/orientationroadtests" className="home-training-tile home-delay-65">
                    <div className="home-training-tile__icon">
                      <OrientationReportSvg />
                    </div>
                    <div className="home-training-tile__label">Road Tests</div>
                  </Link>
                  <Link to="/retest" className="home-training-tile home-delay-70">
                    <div className="home-training-tile__icon">
                      <LoadHistorySvg />
                    </div>
                    <div className="home-training-tile__label">Re-test</div>
                  </Link>
                </div>
              </>
            ) : null}

            {trainingTiles.length > 0 ? (
              <>
                <div className="home-section-label">Training</div>
                <div className="home-training-grid">
                  {trainingTiles.map(tile => (
                    <Link
                      key={tile.to + tile.label}
                      to={tile.to}
                      className={`home-training-tile ${tile.span2 ? 'home-training-tile--span2' : ''} ${tile.delay}`}
                    >
                      <div className="home-training-tile__icon">{tile.icon}</div>
                      <div className="home-training-tile__label">{tile.label}</div>
                    </Link>
                  ))}
                </div>
              </>
            ) : null}

            <div className="home-contacts" id="WalkThroughFeature3">
              <div className="home-contacts__title">Looking to get in touch?</div>
              <button
                type="button"
                className="home-action home-action--contact home-contacts__call-dm home-delay-100"
                onClick={e => {
                  e.stopPropagation();
                  callAgent();
                }}
              >
                <div className="home-action__icon">
                  <DmPhoneSvg />
                </div>
                <div className="home-action__label">Call Agent</div>
                <ChevronRight />
              </button>
              <button
                type="button"
                className="home-action home-action--contact"
                onClick={e => {
                  e.stopPropagation();
                  setShowDM(true);
                }}
              >
                <div className="home-action__icon">
                  <TraineeProfileSvg />
                </div>
                <div className="home-action__label">View Agent Info</div>
                <ChevronRight />
              </button>
              <div className="home-contacts__subtitle">Need more specific help?</div>
              <div className="home-contacts__pair">
                <ContactButton to="/chainofcommand" label="Company Directory" icon={<ChainOfContactSvg />} side="left" />
                <ContactButton to="/safetycontacts" label="Safety Contact" icon={<SafetyContactsSvg />} side="right" />
              </div>
              <div className="home-contacts__pair">
                <ContactButton to="/helpdesk" label="FAQ" icon={<FaqSvg />} side="left" />
                <ContactButton to="/accidentinfo" label="Accidents" icon={<AccidentSvg />} side="right" />
              </div>   
            </div>
            <Link to="/registervehicle" className="home-register home-delay-125">
              <div className="home-action__icon">
                <LicensePlateSvg />
              </div>
              <div className="home-register__label">Register personal vehicle</div>
              <ChevronRight />
            </Link>
          </div>
        </div>
      </IonContent>
      <IonFooter>
        <Nav pageName="Home" />
      </IonFooter>
    </IonPage>
  );
};

export default Home;
